/**
 * The dashboard's request arguments, built in one place so the boot preload
^ * (`app/preloadDashboardRoute`) and the dashboard widgets ask for exactly the same
 * thing — RTK keys its cache by these arguments, so any difference would be a
 * second request instead of a shared one.
 */

interface Sort {
  element?: string;
  order?: string;
}

interface JobOption {
  id: string | number;
}

export interface ActivitiesState {
  sort?: Sort;
  currentTab?: string;
  jobScope?: string;
  filters?: { ats_job_id?: JobOption[] };
}

type FilterBy = Record<string, unknown>;

/** Applications in "Application Review", counted per job (one page). */
export const applicationReviewArgs = (page = 1) => ({
  params: {
    filter_by: { ats_job_stage_names: ['Application Review'] },
    group_by: 'ats_job',
    metric: 'count',
    items: 10,
    page,
  },
});

/** The next three interviews; the endpoint adds "from now" itself. */
export const upcomingInterviewsArgs = () => ({
  params: { items: 3, sort_by: { start_time: 'asc' } },
});

// "My jobs" narrows to the current user, "specific jobs" to the chosen ones
// unless "All" is picked; "all jobs" adds nothing.
const jobScopeFilter = (
  { jobScope, filters }: ActivitiesState,
  userId: string | number | undefined
): FilterBy => {
  if (jobScope === 'my-jobs') return { user_ids: [userId] };
  if (jobScope === 'specific-jobs' && filters?.ats_job_id?.[0]?.id !== 'all') {
    return { ats_job_ids: filters?.ats_job_id?.map((job) => job.id) };
  }
  return {};
};

/** Pending-activity counts per tab, for the current job scope. */
export const activitiesMetricsArgs = (
  activities: ActivitiesState,
  userId: string | number | undefined
) => {
  const filterBy = jobScopeFilter(activities, userId);
  return {
    params: Object.keys(filterBy).length ? { filter_by: filterBy } : {},
  };
};

/** The first five pending activities of the current tab. */
export const activitiesArgs = (
  activities: ActivitiesState,
  userId: string | number | undefined
) => {
  const { sort, currentTab } = activities;
  const tabFilter =
    currentTab === 'overdue'
      ? { overdue: true }
      : { activity_status: currentTab };
  return {
    params: {
      sort_by: { [sort?.element as string]: sort?.order },
      filter_by: { ...tabFilter, ...jobScopeFilter(activities, userId) },
      items: 5,
    },
  };
};

/** The default page size each per-type approvals list was fetched with. */
export const APPROVALS_PER_TYPE = 20;

/** The open-tasks panel's three lists (approvals as one unified list). */
export const openTasksArgs = (
  sort: Sort | undefined,
  isRequisitionsEnabled: boolean
) => {
  const params = { sort_by: { [sort?.element as string]: sort?.order } };
  return {
    scorecards: { params },
    // Surveys sort by when they were sent rather than created.
    surveys: { params: { sort_by: { sent_at: sort?.order } } },
    // The panel does not paginate, so ask for as many rows as the per-type
    // lists used to add up to (a default page of 20 from each).
    approvals: {
      params: {
        ...params,
        items: APPROVALS_PER_TYPE * (isRequisitionsEnabled ? 3 : 2),
      },
    },
  };
};
